import {
  INewRouteApplication,
  INewRouteApplicationDetail,
  INewRouteApplicationsCollection,
  INoticeInfoData,
  IQnaInfo,
  IQnaInfosCollection,
  IVocCollection,
  IVocDetail,
} from "../domain/entities/bbs";
import { defineStore } from "pinia";
import { IBbsRepository } from "@/repositories/bbs/bbsRepository.types";
import { container } from "@/ioc/inversify.config";
import TYPES from "@/ioc/identifiers";
import { IResultData } from "@/domain/entities/shared/result";
import useSetResultCodeIfNeeded from "@/use/useSetResultCodeIfNeeded";
import { IQnaDetail } from "@/domain/entities/bbs/qnaDetail.types";
import { BbsTypeCode, LostFoundType, TalkTypeCode } from "@/types";
import { ILostFoundsCollection } from "@/domain/entities/bbs/lostFound/lostFound.types";
import { ILostFoundDetail } from "@/domain/entities/bbs/lostFound/lostFoundDetail.types";

interface IBbsState {
  noticeInfos: INoticeInfoData[];
  newRouteApplications: INewRouteApplication[];
  qnaInfos: IQnaInfo[];
  lostFoundDetail: ILostFoundDetail | undefined;
}
const bbsRepository = () => {
  return container.get<IBbsRepository>(TYPES.IBbsRepository);
};

export const useBbsStore = defineStore("bbs", {
  state: (): IBbsState => {
    return {
      noticeInfos: [],
      newRouteApplications: [],
      qnaInfos: [],
      lostFoundDetail: undefined,
    };
  },
  getters: {},
  actions: {
    async getNoticeInfos(): Promise<IResultData<INoticeInfoData[]>> {
      const noticeInfosResult: IResultData<INoticeInfoData[]> = {
        isSuccess: false,
        data: [],
      };

      await bbsRepository()
        .getNoticeInfos()
        .then((result) => {
          result
            .map((noticeInfos) => {
              noticeInfosResult.isSuccess = true;
              noticeInfosResult.data = noticeInfos.filter(
                (noticeInfo) => noticeInfo.isShow
              );
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(noticeInfosResult, error);
              noticeInfosResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          noticeInfosResult.errorMessage = error.message;
        });

      this.noticeInfos = noticeInfosResult.data ?? [];
      return noticeInfosResult;
    },

    async readNoticeInfo(id: string): Promise<IResultData<INoticeInfoData[]>> {
      const readNoticeResult: IResultData<INoticeInfoData[]> = {
        isSuccess: false,
        data: [],
      };

      await bbsRepository()
        .readNoticeInfo(id)
        .then((result) => {
          result
            .map(() => {
              this.noticeInfos = this.noticeInfos.map((noticeInfo) => {
                if (noticeInfo.id === id) {
                  noticeInfo.wasRead = true;
                }
                return noticeInfo;
              });
              readNoticeResult.isSuccess = true;
              readNoticeResult.data = this.noticeInfos;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(readNoticeResult, error);
              readNoticeResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          readNoticeResult.errorMessage = error.message;
        });

      return readNoticeResult;
    },

    async getNewRouteApplications(
      searchText: string,
      offset: number,
      limit?: number,
      myPostOnly?: boolean,
      todayYn?: boolean
    ): Promise<IResultData<INewRouteApplicationsCollection>> {
      const applicationsResult: IResultData<INewRouteApplicationsCollection> = {
        isSuccess: false,
      };

      await bbsRepository()
        .getNewRouteApplications(searchText, offset, limit, myPostOnly, todayYn)
        .then((result) => {
          result
            .map((applications) => {
              applicationsResult.isSuccess = true;
              applicationsResult.data = applications;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(applicationsResult, error);
              applicationsResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          applicationsResult.errorMessage = error.message;
        });

      this.newRouteApplications = applicationsResult.data?.list ?? [];
      return applicationsResult;
    },

    async getNewRouteApplicationDetail(
      uid: string
    ): Promise<IResultData<INewRouteApplicationDetail>> {
      const applicationDetailResult: IResultData<INewRouteApplicationDetail> = {
        isSuccess: false,
      };

      await bbsRepository()
        .getNewRouteApplicationDetail(uid)
        .then((result) => {
          result
            .map((applications) => {
              applicationDetailResult.isSuccess = true;
              applicationDetailResult.data = applications;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(applicationDetailResult, error);
              applicationDetailResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          applicationDetailResult.errorMessage = error.message;
        });

      return applicationDetailResult;
    },

    async applyNewRouteApplication(
      newRouteDetail: INewRouteApplicationDetail
    ): Promise<IResultData<boolean>> {
      const createResult: IResultData<boolean> = {
        isSuccess: false,
      };

      await bbsRepository()
        .applyNewRouteApplication(newRouteDetail)
        .then((result) => {
          result
            .map((result) => {
              createResult.isSuccess = true;
              createResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(createResult, error);
              createResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          createResult.errorMessage = error.message;
        });

      return createResult;
    },

    async editNewRouteApplication(
      talkUid: string,
      newRouteDetail: INewRouteApplicationDetail
    ): Promise<IResultData<boolean>> {
      const editResult: IResultData<boolean> = {
        isSuccess: false,
      };

      await bbsRepository()
        .editNewRouteApplication(talkUid, newRouteDetail)
        .then((result) => {
          result
            .map((result) => {
              editResult.isSuccess = true;
              editResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(editResult, error);
              editResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          editResult.errorMessage = error.message;
        });

      return editResult;
    },

    async deleteNewRouteApplication(
      routeApplicationUid: string
    ): Promise<IResultData<boolean>> {
      const deleteResult: IResultData<boolean> = {
        isSuccess: false,
      };

      await bbsRepository()
        .deleteNewRouteApplication(routeApplicationUid)
        .then((result) => {
          result
            .map((result) => {
              deleteResult.isSuccess = true;
              deleteResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(deleteResult, error);
              deleteResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          deleteResult.errorMessage = error.message;
        });

      return deleteResult;
    },

    async receiveNewRouteApplicationResult(
      routeApplicationUid: string
    ): Promise<IResultData<boolean>> {
      const receiveResult: IResultData<boolean> = {
        isSuccess: false,
      };

      await bbsRepository()
        .receiveNewRouteApplicationResult(routeApplicationUid)
        .then((result) => {
          result
            .map((result) => {
              receiveResult.isSuccess = true;
              receiveResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(receiveResult, error);
              receiveResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          receiveResult.errorMessage = error.message;
        });

      return receiveResult;
    },

    async getRecentlyUsedLines(
      boardDateFrom?: string,
      boardDateTo?: string
    ): Promise<IResultData<string[]>> {
      const recentlyUsedLines: IResultData<string[]> = {
        isSuccess: false,
        data: [],
      };

      await bbsRepository()
        .getRecentlyUsedLines(boardDateFrom, boardDateTo)
        .then((result) => {
          result
            .map((lineNames) => {
              recentlyUsedLines.isSuccess = true;
              recentlyUsedLines.data = lineNames.data;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(recentlyUsedLines, error);
              recentlyUsedLines.errorMessage = error.message;
            });
        })
        .catch((error) => {
          recentlyUsedLines.errorMessage = error.message;
        });

      return recentlyUsedLines;
    },

    async getQnaList(
      bbsType: BbsTypeCode,
      talkTypeCode: TalkTypeCode,
      searchText: string,
      myPostOnly: boolean,
      offset: number,
      limit?: number,
      todayYn?: boolean
    ): Promise<IResultData<IQnaInfosCollection>> {
      const qnaInfosResult: IResultData<IQnaInfosCollection> = {
        isSuccess: false,
      };

      await bbsRepository()
        .getQnaList(
          bbsType,
          talkTypeCode,
          searchText,
          myPostOnly,
          offset,
          limit,
          todayYn
        )
        .then((result) => {
          result
            .map((qnaList) => {
              qnaInfosResult.isSuccess = true;
              qnaInfosResult.data = qnaList;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(qnaInfosResult, error);
              qnaInfosResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          qnaInfosResult.errorMessage = error.message;
        });

      this.qnaInfos = qnaInfosResult.data?.list ?? [];
      return qnaInfosResult;
    },

    async getQnaDetail(uid: string): Promise<IResultData<IQnaDetail>> {
      const qnaDetailResult: IResultData<IQnaDetail> = {
        isSuccess: false,
      };

      await bbsRepository()
        .getQnaDetail(uid)
        .then((result) => {
          result
            .map((detailInfo) => {
              qnaDetailResult.isSuccess = true;
              qnaDetailResult.data = detailInfo;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(qnaDetailResult, error);
              qnaDetailResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          qnaDetailResult.errorMessage = error.message;
        });
      return qnaDetailResult;
    },

    async createQnaDetail(
      qnaDetail: IQnaDetail,
      formData: FormData
    ): Promise<IResultData<boolean>> {
      const createResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bbsRepository()
        .createQnaDetail(qnaDetail, formData)
        .then((result) => {
          result
            .map((_) => {
              createResult.isSuccess = true;
              createResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(createResult, error);
              createResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          createResult.errorMessage = error.message;
        });
      return createResult;
    },

    async updateQnaDetail(
      qnaDetail: IQnaDetail,
      formData: FormData
    ): Promise<IResultData<boolean>> {
      const updateResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bbsRepository()
        .updateQnaDetail(qnaDetail, formData)
        .then((result) => {
          result
            .map((_) => {
              updateResult.isSuccess = true;
              updateResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(updateResult, error);
              updateResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          updateResult.errorMessage = error.message;
        });
      return updateResult;
    },

    async deleteQnaDetail(talkUid: string): Promise<IResultData<boolean>> {
      const deleteResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bbsRepository()
        .deleteQnaDetail(talkUid)
        .then((result) => {
          result
            .map((_) => {
              deleteResult.isSuccess = true;
              deleteResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(deleteResult, error);
              deleteResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          deleteResult.errorMessage = error.message;
        });
      return deleteResult;
    },

    async getVocList(
      codeGroup: string,
      searchText: string,
      offset: number,
      limit?: number,
      todayYn?: boolean,
      myPostOnly?: boolean
    ): Promise<IResultData<IVocCollection>> {
      const vocListResult: IResultData<IVocCollection> = {
        isSuccess: false,
      };

      await bbsRepository()
        .getVocList(codeGroup, searchText, offset, limit, todayYn, myPostOnly)
        .then((result) => {
          result
            .map((qnaList) => {
              vocListResult.isSuccess = true;
              vocListResult.data = qnaList;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(vocListResult, error);
              vocListResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          vocListResult.errorMessage = error.message;
        });

      return vocListResult;
    },

    async getVocDetail(uid: string): Promise<IResultData<IVocDetail>> {
      const vocDetailResult: IResultData<IVocDetail> = {
        isSuccess: false,
      };

      await bbsRepository()
        .getVocDetail(uid)
        .then((result) => {
          result
            .map((detailInfo) => {
              vocDetailResult.isSuccess = true;
              vocDetailResult.data = detailInfo;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(vocDetailResult, error);
              vocDetailResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          vocDetailResult.errorMessage = error.message;
        });
      return vocDetailResult;
    },

    async createVocDetail(
      vocDetail: IVocDetail,
      formData: FormData
    ): Promise<IResultData<boolean>> {
      const createResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bbsRepository()
        .createVocDetail(vocDetail, formData)
        .then((result) => {
          result
            .map((_) => {
              createResult.isSuccess = true;
              createResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(createResult, error);
              createResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          createResult.errorMessage = error.message;
        });
      return createResult;
    },

    async updateVocDetail(
      vocDetail: IVocDetail,
      formData: FormData
    ): Promise<IResultData<boolean>> {
      const updateResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bbsRepository()
        .updateVocDetail(vocDetail, formData)
        .then((result) => {
          result
            .map((_) => {
              updateResult.isSuccess = true;
              updateResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(updateResult, error);
              updateResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          updateResult.errorMessage = error.message;
        });
      return updateResult;
    },

    async deleteVocDetail(uid: string): Promise<IResultData<boolean>> {
      const deleteResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bbsRepository()
        .deleteVocDetail(uid)
        .then((result) => {
          result
            .map((_) => {
              deleteResult.isSuccess = true;
              deleteResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(deleteResult, error);
              deleteResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          deleteResult.errorMessage = error.message;
        });
      return deleteResult;
    },

    async getLostFoundList(
      lostFoundType: LostFoundType,
      searchText: string,
      offset: number,
      limit?: number,
      todayYn?: boolean
    ): Promise<IResultData<ILostFoundsCollection>> {
      const fetchResult: IResultData<ILostFoundsCollection> = {
        isSuccess: false,
      };

      await bbsRepository()
        .getLostFoundList(lostFoundType, searchText, offset, limit, todayYn)
        .then((result) => {
          result
            .map((resData) => {
              fetchResult.isSuccess = true;
              fetchResult.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(fetchResult, error);
              fetchResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          fetchResult.errorMessage = error.message;
        });
      return fetchResult;
    },

    async getLostFoundDetail(
      uid: string
    ): Promise<IResultData<ILostFoundDetail>> {
      const lostFoundDetailResult: IResultData<ILostFoundDetail> = {
        isSuccess: false,
      };

      await bbsRepository()
        .getLostFoundDetail(uid)
        .then((result) => {
          result
            .map((detailInfo) => {
              lostFoundDetailResult.isSuccess = true;
              lostFoundDetailResult.data = detailInfo;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(lostFoundDetailResult, error);
              lostFoundDetailResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          lostFoundDetailResult.errorMessage = error.message;
        });

      this.lostFoundDetail = lostFoundDetailResult.data;
      return lostFoundDetailResult;
    },

    async createLostFoundDetail(
      lostFoundDetail: ILostFoundDetail
    ): Promise<IResultData<boolean>> {
      const createResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bbsRepository()
        .createLostFoundDetail(lostFoundDetail)
        .then((result) => {
          result
            .map((_) => {
              createResult.isSuccess = true;
              createResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(createResult, error);
              createResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          createResult.errorMessage = error.message;
        });
      return createResult;
    },

    async updateLostFoundDetail(
      lostFoundDetail: ILostFoundDetail
    ): Promise<IResultData<boolean>> {
      const updateResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bbsRepository()
        .updateLostFoundDetail(lostFoundDetail)
        .then((result) => {
          result
            .map((_) => {
              updateResult.isSuccess = true;
              updateResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(updateResult, error);
              updateResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          updateResult.errorMessage = error.message;
        });
      return updateResult;
    },

    async deleteLostFoundDetail(uid: string): Promise<IResultData<boolean>> {
      const deleteResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await bbsRepository()
        .deleteLostFoundDetail(uid)
        .then((result) => {
          result
            .map((_) => {
              deleteResult.isSuccess = true;
              deleteResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(deleteResult, error);
              deleteResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          deleteResult.errorMessage = error.message;
        });
      return deleteResult;
    },
  },
});
